/*
 * ves-reveal.js: one shared IntersectionObserver that drives every scroll
 * reveal on the site. Elements opt in with `data-ves-reveal-on-scroll` (the
 * whole element rises + fades) or `data-ves-reveal-children` (its direct
 * children cascade, staggered by position). When an element scrolls into view
 * the observer adds `.ves-in` and stops watching it. All the motion lives in
 * CSS (translate + opacity only); this file just flips the class.
 *
 * Cost: one observer for the whole page, no scroll listeners, ~1 KB. The hidden
 * state is gated in CSS behind `(prefers-reduced-motion: no-preference) and
 * (scripting: enabled)`, so content is always visible if motion is reduced, JS
 * is off, or the browser is old; this script only ever reveals, never hides.
 *
 * A second observer (the "animation gate") continuously toggles
 * `.ves-anim-paused` on elements that opt in with `data-ves-anim-pause-offscreen`
 * (the showcase marquees). The CSS decides whether that class actually halts the
 * animation, and it only does so on small viewports, so off-screen marquees stop
 * animating on mobile (relieving GPU layer pressure) while desktop is untouched.
 * The gate only ever pauses while off-screen and resumes on-screen, so a JS
 * failure can never freeze a marquee.
 */
(function () {
  var SELECTOR = '[data-ves-reveal-on-scroll], [data-ves-reveal-children]';
  var GATE_SELECTOR = '[data-ves-anim-pause-offscreen]';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var io = null;
  var gate = null;

  function reveal(el) {
    el.classList.add('ves-in');
  }

  function ensureObserver() {
    if (io || !('IntersectionObserver' in window)) return io;
    io = new IntersectionObserver(function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        // Reveal when in view, or when the element is already scrolled past (its
        // bottom is at/above the top edge) so a deep-link jump to a mid-page
        // anchor never leaves the sections above it stuck hidden.
        if (e.isIntersecting || e.boundingClientRect.bottom <= 0) {
          reveal(e.target);
          obs.unobserve(e.target);
        }
      }
    }, {
      // Trigger a hair before the element is fully on screen so the entrance
      // reads as the section "arriving", not popping in after it's already there.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    });
    return io;
  }

  function ensureGate() {
    if (gate || !('IntersectionObserver' in window)) return gate;
    gate = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (e.isIntersecting) {
          e.target.classList.remove('ves-anim-paused');
        } else {
          e.target.classList.add('ves-anim-paused');
        }
      }
    }, {
      // Resume a little before the marquee enters view so it never reads as frozen.
      rootMargin: '200px 0px 200px 0px'
    });
    return gate;
  }

  function bindReveal() {
    var nodes = document.querySelectorAll(SELECTOR);
    if (!nodes.length) return;
    // No IntersectionObserver (very old browser): reveal everything now.
    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < nodes.length; i++) reveal(nodes[i]);
      return;
    }
    var obs = ensureObserver();
    for (var j = 0; j < nodes.length; j++) {
      var n = nodes[j];
      if (n.dataset.vesRevealBound === '1') continue;
      n.dataset.vesRevealBound = '1';
      obs.observe(n);
    }
  }

  function bindGate() {
    if (!('IntersectionObserver' in window)) return;
    var nodes = document.querySelectorAll(GATE_SELECTOR);
    if (!nodes.length) return;
    var g = ensureGate();
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.dataset.vesGateBound === '1') continue;
      n.dataset.vesGateBound = '1';
      g.observe(n);
    }
  }

  function init() {
    // Reduced motion: CSS keeps everything visible and animations off; nothing
    // to do for either observer.
    if (reduce) return;
    bindReveal();
    bindGate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-scan when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
